import { Component, ElementRef, HostListener, OnInit, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { DialItem, DialItemClickEvent } from '@progress/kendo-angular-buttons';
import { commentIcon, gearsIcon, imageAddIcon, imageIcon, SVGIcon, handIcon, xCircleIcon, pencilIcon } from '@progress/kendo-svg-icons';
import { RtaService } from '../../shared/services/rta.service';
import { RTAApiClient } from '../../shared/services/rta-api.service';
import { Message, User, SendMessageEvent, ExecuteActionEvent } from '@progress/kendo-angular-conversational-ui';
import { TextAreaComponent } from '@progress/kendo-angular-inputs';
import { AutoCompleteComponent } from '@progress/kendo-angular-dropdowns';
import { Select, Store } from '@ngxs/store';
import { Observable, takeUntil, map, filter, Subject, combineLatest, finalize } from 'rxjs';
import { FixedCutterInput, IORD_Order, ITaskViewModel, PatchORDOrderOnHoldDto } from '../../shared/interfaces/request.interface';
import { AccountActions } from '../../shared/store/action/account.action';
import { OptionActions } from '../../shared/store/action/options.action';
import { RequestActions } from '../../shared/store/action/request.action';
import { RequestState } from '../../shared/store/state/request.state';
import { OptionState } from '../../shared/store/state/options.state';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AvailableCutter } from '../../shared/interfaces/cutterMap';
import { GlobalNotificationService } from '../../shared/services/globalnotification.service';
import { SignalRService } from '../../shared/services/signalr.service';

declare var CURRENT_USER_ID: number;
declare var CURRENT_USER_LOGINID: number;
declare var CURRENT_USER_DISPLAYNAME: string;

@Component({
    selector: 'app-ejdf',
    templateUrl: './ejdf.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./ejdf.component.css'],
    standalone: false
})
export class EjdfComponent implements OnInit, OnDestroy {

  @ViewChild("messageBoxInput", { static: false })
  public messageBoxInput!: TextAreaComponent;
  @ViewChild('messageBoxInput') textarea!: ElementRef;
  @ViewChild('autocomplete') autocomplete!: AutoCompleteComponent;

  @ViewChild("uploadInput", { static: false })
  public inputFile!: ElementRef;
  
  public request$!: Observable<IORD_Order | undefined>;
    private destroy$ = new Subject<void>();

  public innerInputFocus = false;
  public selectedImage!: File;
  public xCircleIcon: SVGIcon = xCircleIcon;
  public imageIcon: SVGIcon = imageIcon;
  public messageText: string = '';

  public showOnHold: boolean = false;

  public users: any[] = [];

  public windowTop = 100;
  public windowLeft = 50;

  // Drawing Number Dialog
  public showDrawingDialog: boolean = false;
  public drawingName: string = '';
  public drawingDescription: string = '';
  public drawingSubmitting: boolean = false;
  public pencilIcon: SVGIcon = pencilIcon;

  constructor(
    private fb: FormBuilder, 
    private service: RtaService, 
    private rtaApiClient: RTAApiClient,
    private route: ActivatedRoute, 
    private router: Router, 
    private titleService: Title, 
    private store: Store,
    private notificationService: GlobalNotificationService,
    private signalRService: SignalRService
  ) {}
  
  public id!: number;
  public designReferenceNumber!: number;
  public imageAddSVG: SVGIcon = imageAddIcon;
  public CommentSVG: SVGIcon = commentIcon;
  public GearsSVG: SVGIcon = gearsIcon;
  public viewChat: boolean = false;
  public _communications: Message[] = [];

  // Observables for cutter type lookup
  public availableCutters$!: Observable<AvailableCutter[]>;
  public cutterType$!: Observable<string>;
  public currentCutterType: string = 'Pending';
  public materialNumber: string = '';

  public actionItems: Array<DialItem> = [];
  public fileListRefreshToken = 0;


  currentUserId: number = CURRENT_USER_ID;

  isLoading = true;
  isReviewDataLoading = true;
  currentSection: string = "requestdetails";
  selectedIndex = 0;

  ngOnInit(): void {
    // Initialize SignalR connection at the parent level
    this.initializeSignalR();
    
    // Initialize available cutters observable
    this.availableCutters$ = this.store.select(OptionState.availableCutters);
    
    this.route.paramMap.pipe(
      takeUntil(this.destroy$),
      map((params: ParamMap) => parseInt(params.get('id') || '', 10)),
      filter(id => !isNaN(id))
    ).subscribe(id => {
      this.id = id;
      
      // Join the order's SignalR group for real-time updates
      this.joinOrderSignalRGroup(id);
      
      this.store.dispatch(new RequestActions.GetRequestDetails({ id: this.id.toString() }));      
      this.store.dispatch(new AccountActions.GetAllUserDetails());
      this.store.dispatch(new OptionActions.GetBomLayout({id: this.id}));
      this.store.dispatch(new OptionActions.GetAvailableCutters({ id: id, sapnumber: 0 }));
      this.store.dispatch(new RequestActions.LoadTasks(this.id));
      this.store.dispatch(new RequestActions.GetFixedCutterPreBOM({ id: this.id}));
      this.request$ = this.store.select(RequestState.getById).pipe(
        map(getByIdFn => getByIdFn(this.id))
      );
      
      // Initialize cutter type observable - combines request and available cutters
      this.cutterType$ = combineLatest([
        this.request$,
        this.availableCutters$
      ]).pipe(
        map(([request, cutters]) => {
          if (!request?.orD_CutterMap || !cutters?.length) {
            return 'Pending';
          }
          
          // Find first SHOULDER cutter on blade 1, row 1
          const shoulderCutter = request.orD_CutterMap.find(
            c => c.zone?.toUpperCase() === 'SHOULDER' && 
                 c.bladeNumber === 1 && 
                 c.rowNumber === 1
          );
          
          if (!shoulderCutter?.pdcCutter_Id) {
            return 'Pending';
          }
          
          // Look up the cutter name from available cutters
          const matchedCutter = cutters.find(
            c => c.pdcCutterID === shoulderCutter.pdcCutter_Id
          );
          
          return matchedCutter?.name ?? 'Pending';
        })
      );
      
      // Subscribe to cutter type to store the current value for click handlers
      this.cutterType$.pipe(takeUntil(this.destroy$)).subscribe(cutterType => {
        this.currentCutterType = cutterType;
      });
      
      this.request$.pipe(takeUntil(this.destroy$)).subscribe(request => {
        if (request) {
          this.materialNumber = this.getMaterialNumber(request);
          this.actionItems = [
            { svgIcon: this.imageAddSVG, label: "Request Drawing Number" },
            { svgIcon: this.CommentSVG, label: "Open Chat" }
          ];
          if(request.onHold) {
            this.actionItems.push({ svgIcon: handIcon, label: "Remove On Hold" });
          }
        }
      });
    });
  }
  ngOnDestroy(): void {
    // Leave SignalR order group and stop connection
    if (this.id) {
      this.signalRService.leaveOrderGroup(this.id).catch(() => {});
    }
    this.signalRService.stopConnection().catch(() => {});
    
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize SignalR connection at the parent component level.
   * Child components can subscribe to events without starting their own connection.
   */
  private initializeSignalR(): void {
    this.signalRService.startConnection()
      .then(() => {
        console.log('✅ EJDF: SignalR connection established');
      })
      .catch(err => {
        console.warn('⚠️ EJDF: SignalR connection failed, real-time updates unavailable:', err);
      });
  }

  /**
   * Join the order-specific SignalR group for real-time updates.
   * This allows all users viewing this order to receive updates.
   */
  private joinOrderSignalRGroup(orderId: number): void {
    // Wait a short time to ensure connection is established
    setTimeout(() => {
      if (this.signalRService.isConnected()) {
        this.signalRService.joinOrderGroup(orderId)
          .then(() => {
            console.log(`✅ EJDF: Joined SignalR group for order ${orderId}`);
          })
          .catch(err => {
            console.warn(`⚠️ EJDF: Failed to join order group ${orderId}:`, err);
          });
      } else {
        console.warn('⚠️ EJDF: SignalR not connected, cannot join order group');
      }
    }, 500);
  }

  public getCurrentUser(): User {
    return { id: CURRENT_USER_ID, name: `${this.service.$CURRENT_USER_DISPLAYNAME} (${this.service.$CURRENT_USER_LOGINID})` }
  }
  public onDialItemClick(e: DialItemClickEvent): void {
    if (e.item.label === "Open Chat")
      this.viewChat = true;
    else if (e.item.label === "Request Drawing Number") {
      this.openDrawingDialog();
    }
    else if (e.item.label === "Remove On Hold") {
      this.store.dispatch(new RequestActions.RemoveHold(this.id));
    }
    const args = { item: e.item, index: e.index };
  }

  /**
   * Opens the Request Drawing Number dialog
   */
  public openDrawingDialog(): void {
    this.drawingName = '';
    this.drawingDescription = '';
    this.showDrawingDialog = true;
  }

  /**
   * Closes the Request Drawing Number dialog
   */
  public closeDrawingDialog(): void {
    this.showDrawingDialog = false;
    this.drawingName = '';
    this.drawingDescription = '';
  }

  /**
   * Submits the drawing number request
   */
  public submitDrawingRequest(): void {
    if (!this.drawingName?.trim()) {
      this.notificationService.showWarningNotification('Please enter a drawing name');
      return;
    }

    this.drawingSubmitting = true;
    
    this.rtaApiClient.createDrawing(
      this.drawingName.trim(),
      this.drawingDescription?.trim() || '',
      CURRENT_USER_ID,
      this.id
    ).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.drawingSubmitting = false)
    ).subscribe({
      next: (response) => {
        if (response?.status) {
          const drawingNumber = response.info?.drawingNumber || response.info;
          this.notificationService.showSuccessNotification(
            `Drawing number requested successfully${drawingNumber ? ': ' + drawingNumber : ''}`
          );
          this.closeDrawingDialog();
          // Refresh the request to get updated data
          this.store.dispatch(new RequestActions.GetRequestDetails({ id: this.id.toString() }));
        } else {
          this.notificationService.showErrorNotification(
            response?.message || 'Failed to request drawing number'
          );
        }
      },
      error: (error) => {
        console.error('Drawing request failed:', error);
        this.notificationService.showErrorNotification('Failed to request drawing number. Please try again.');
      }
    });
  }

  public PopdataLoaded(obj: FixedCutterInput | null | undefined): boolean 
  { return obj !== null && obj !== undefined && Object.keys(obj).length > 1 }

  /**
   * Gets the service number from SapNumbers where sapNumTypeId === 9
   */
  public getServiceNumber(request: IORD_Order): string {
    if (!request?.sapNumbers?.length) {
      return 'Pending';
    }
    const serviceNum = request.sapNumbers.find(s => s.sapNumTypeId === 9);
    return serviceNum?.number?.toString() ?? 'Pending';
  }

  /**
   * Gets the material number from SapNumbers where sapNumTypeId === 3
   */
  public getMaterialNumber(request: IORD_Order): string {
    if (!request?.sapNumbers?.length) {
      return 'Pending';
    }
    const materialNum = request.sapNumbers.find(s => s.sapNumTypeId === 3);
    return materialNum?.number?.toString() ?? 'Pending';
  }

  /**
   * Copies text to clipboard with notification
   */
  public copyToClipboard(text: string, label: string): void {
    if (!text || text === 'Pending') {
      this.notificationService.showInfoNotification(`No ${label} available to copy`);
      return;
    }
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        this.notificationService.showSuccessNotification(`${label} copied to clipboard`);
      }).catch(() => {
        this.fallbackCopyToClipboard(text, label);
      });
    } else {
      this.fallbackCopyToClipboard(text, label);
    }
  }

  private fallbackCopyToClipboard(text: string, label: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      this.notificationService.showSuccessNotification(`${label} copied to clipboard`);
    } catch (err) {
      this.notificationService.showErrorNotification(`Failed to copy ${label}`);
    }
    document.body.removeChild(textArea);
  }

  /**
   * Checks if the request has high priority
   */
  public isHighPriority(request: IORD_Order): boolean {
    const priority = (request?.priority ?? '').toString().toLowerCase();
    return ['high', 'urgent', 'critical'].includes(priority);
  }

  public onFileSelect(event: Event): void {
    
  }
  
  public onAction(e: ExecuteActionEvent): void {
    if (e.action.type === "reply") {
      this.inputFile.nativeElement.click();
    }
    this.messageBoxInput.focus();
  }

  public onUploadCompleted(item: any): void {
    this.fileListRefreshToken++;
  }
  

}
